// `usePhone` — a page's side of the phone rule (./phoneViewport.js).
//
// Returns whether the page should draw its phone layout, kept in step with
// the window, and — unless told not to — claims the device-width viewport
// for as long as the page is open, so the phone layout arrives at device
// width instead of as a shrunken photograph of a desktop one. The shell
// stays the only writer of the viewport tag; this only asks.

import { useEffect, useState } from 'react';
import { useInRouterContext, useLocation } from 'react-router-dom';
import { PHONE_MAX, detectPhone, claimPhoneViewport } from './phoneViewport';

export function usePhone({ max = PHONE_MAX, claimViewport = true } = {}) {
  const [narrow, setNarrow] = useState(() => detectPhone(max));
  // The page's OWN address: inside a tab that is the tab's location, which
  // stays put while another tab is in front. Router context does not change
  // for a mounted component, so the conditional hook is stable.
  const inRouter = useInRouterContext();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const routed = inRouter ? useLocation().pathname : null;

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const release = claimViewport ? claimPhoneViewport(routed ?? window.location.pathname) : null;

    const on = () => setNarrow(detectPhone(max));
    const mq = typeof window.matchMedia === 'function' ? window.matchMedia(`(max-width: ${max}px)`) : null;
    if (mq && mq.addEventListener) mq.addEventListener('change', on);
    else if (mq && mq.addListener) mq.addListener(on);
    // The media query does not fire when the claim above changes the
    // viewport out from under it, so the first measurement is taken again
    // on the next frame.
    const t = setTimeout(on, 0);
    window.addEventListener('orientationchange', on);

    return () => {
      clearTimeout(t);
      window.removeEventListener('orientationchange', on);
      if (mq && mq.removeEventListener) mq.removeEventListener('change', on);
      else if (mq && mq.removeListener) mq.removeListener(on);
      if (release) release();
    };
  }, [max, claimViewport, routed]);

  return narrow;
}
